import { type CanvasConfig, type CanvasExportOptions } from './types';

/** Default title assigned to newly created canvases */
export const DEFAULT_CANVAS_TITLE = 'Untitled Canvas';

/** Selection-scoped export unless a caller opts into whole-canvas (`scope: 'all'`). */
export const DEFAULT_CANVAS_EXPORT_OPTIONS: CanvasExportOptions = { scope: 'selection' };

export const SAVE_TO_BOARD_LABEL = 'Save to board';

export const SAVE_AS_NEW_VERSION_LABEL = 'Save as new version';

export const SAVE_TO_VERSION_TARGET_LABEL = 'Save back to original asset';

export const SAVED_TO_VERSION_TARGET_LABEL = 'Saved to original asset';

export const PICK_FROM_BOARD_LABEL = 'Pick from board';

export const SAVE_VERSION_ALL_WORKSPACE_LABEL = 'All workspace';

/** Shown on Save to board when the node is already a workspace original. */
export const ALREADY_SAVED_DESCRIPTION = 'This is saved';

export const SAVE_ALL_TO_BOARD_LABEL = 'Save all to board';

export const SAVE_ALL_TO_WORKSPACE_LABEL = 'Save all to workspace';

export const DOWNLOAD_ALL_LABEL = 'Download all';

/** Whole-canvas save has nothing left to send — shown on the disabled rail row. */
export const ALL_ALREADY_SAVED_DESCRIPTION = 'Everything on this Canvas is already saved';

/** Text-only or otherwise file-less canvas — nothing was saved, there is just nothing to send. */
export const NOTHING_TO_SAVE_DESCRIPTION = 'There are no files on this Canvas to save';

/** Public/view-only canvas, or the host did not pass a save handler. */
export const SAVING_UNAVAILABLE_DESCRIPTION = "Saving isn't available on this Canvas";

export const NOTHING_TO_DOWNLOAD_DESCRIPTION = 'There are no files on this Canvas to download';

/** Public canvas, or the viewer is not allowed to take files off this canvas. */
export const DOWNLOADING_UNAVAILABLE_DESCRIPTION = "Downloading isn't available on this Canvas";

export const ZIP_DOWNLOAD_FAILED_DESCRIPTION = 'Failed to prepare the download';

/** Shown when a second whole-canvas zip is requested while the first is still building. */
export const ZIP_DOWNLOAD_IN_PROGRESS_DESCRIPTION = 'A download is already in progress';

/** Below this a zip is just a wrapper the user has to open, so the file is handed over directly. */
export const ZIP_MINIMUM_FILE_COUNT = 2;

/** Default viewport for the canvas */
export const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 1 } as const;

/** Minimum number of nodes required for distribution */
export const MINIMUM_DISTRIBUTION_COUNT = 3;

/** Size of the thumbnail in the token chip */
export const TOKEN_CHIP_THUMBNAIL_SIZE = 24;

/** Spacing between grid dots (px) */
export const GRID_SIZE = 24;

/** Maximum node width (px). Matches Imgix's per-side limit so resized media stays renderable. */
export const MAX_NODE_WIDTH = 16384;

/** Maximum node height (px). Matches Imgix's per-side limit so resized media stays renderable. */
export const MAX_NODE_HEIGHT = 16384;

/** Minimum zoom level for the canvas */
export const MIN_ZOOM = 0.01;

/** Maximum zoom level for the canvas */
export const MAX_ZOOM = 2;

/** Dot radius at minimum zoom */
export const MIN_DOT_RADIUS = 0.25;

/** Dot radius at maximum zoom */
export const MAX_DOT_RADIUS = 1;

/** Screen-space gap (px) at which dots are fully hidden */
export const MIN_GAP_PX = 4;

/** Screen-space gap (px) at which dots are fully visible */
export const FULL_GAP_PX = 16;

/** Exponent for the density-compensation fade curve (lower = gentler fade at low zoom) */
export const DENSITY_FADE_EXPONENT = 0.05;

/** Base HSL lightness for dot color in light mode (--colors-gray-9) */
export const DOT_LIGHTNESS_LIGHT = 50;

/** Base HSL lightness for dot color in dark mode (--colors-gray-9) */
export const DOT_LIGHTNESS_DARK = 64;

/** Lightness percentage points shifted across the full zoom range */
export const DOT_LIGHTNESS_SHIFT = 4;

/** Default size of the ImageGeneration node */
export const IMAGE_GENERATION_NODE_SIZE = 1024;

/** Standard gap between nodes for layout computations (matches grid dot spacing) */
export const NODE_GAP = 24;

/** Default maximum row width before wrapping to a new row (fallback when viewport unknown) */
export const DEFAULT_MAX_ROW_WIDTH = 64000;

/** Minimum screen-space dimension (px) before showing hover buttons on a node */
export const MIN_NODE_SCREEN_SIZE_FOR_HOVER_BUTTONS = 80;

/** Vertical gap between floating toolbar and selection bounds (px) */
export const TOOLBAR_GAP = 24;

/** Z-index for floating toolbars (multi-select, group) */
export const TOOLBAR_Z_INDEX = 10;

/** Footer toolbar — above `CanvasNullStateLayer` (`z-10`) so empty-canvas actions stay reachable. */
export const CANVAS_FOOTER_ACTIONS_Z_INDEX = TOOLBAR_Z_INDEX + 1;

/** CSS custom property set by SidebarResizer for the chat sidebar width */
export const CHAT_SIDEBAR_WIDTH_CSS_VAR = '--chat-sidebar-width';

/** localStorage key for chat sidebar width */
export const CHAT_SIDEBAR_WIDTH_LOCAL_STORAGE_KEY = 'CHAT_SIDEBAR_WIDTH';

/** Popovers above the size-12 ChatSidebarToggle (toggle bottom + height + gap). */
export const CHAT_SIDEBAR_TOGGLE_OVERLAY_BOTTOM_CLASS = 'bottom-16';
export const CHAT_SIDEBAR_TOGGLE_OVERLAY_FULLSCREEN_BOTTOM_CLASS = 'bottom-24 ui2026:bottom-16';

/** Marks surfaces that own Backspace/Delete so canvas node delete does not fire. */
export const CANVAS_DELETE_OWNER_ATTRIBUTE = 'data-canvas-delete-owner';

/**
 * Stable selector for the image Tools catalog trigger. Transform Back restores
 * focus here so Backspace keeps deleting the selected node after the transform
 * toolbar unmounts (focus would otherwise land in chat).
 */
export const TOOLS_CATALOG_TRIGGER_SELECTOR = '[data-tools-catalog-trigger] button';

/** Marks the canvas image used for asset-modal ↔ canvas view-transition morphs. */
export const CANVAS_MORPH_HERO_IMAGE_ATTRIBUTE = 'data-canvas-morph-hero-image';

export const CANVAS_MORPH_HERO_IMAGE_SELECTOR = `[${CANVAS_MORPH_HERO_IMAGE_ATTRIBUTE}]`;

export const CANVAS_SELECTED_MORPH_HERO_IMAGE_SELECTOR = `.react-flow__node.selected ${CANVAS_MORPH_HERO_IMAGE_SELECTOR}`;

/** Mouse button indices for ReactFlow's panOnDrag */
const LEFT_MOUSE_BUTTON = 0;
const MIDDLE_MOUSE_BUTTON = 1;

/** Node types that are selectable */
export const SELECTABLE_NODE_TYPES = new Set(['image', 'video', 'text']);

/**
 * Backspace/Delete may remove these types. Super-set of SELECTABLE_NODE_TYPES so
 * processing imageGeneration placeholders and upload previews stay dismissable —
 * deselect and Escape leave those in place.
 */
export const DELETABLE_NODE_TYPES = new Set([...SELECTABLE_NODE_TYPES, 'canvasMediaUploadPreview', 'imageGeneration']);

/** Standard interactive mode: nodes are selectable/draggable, pan with middle mouse, selection drag enabled */
/**
 * Marks a portalled surface (e.g. the tools catalog panel or a toolbar menu's content) as part of
 * a node toolbar for the hover toolbar's focus and outside-press logic, even though it is not a
 * DOM descendant of the toolbar wrapper.
 */
export const NODE_TOOLBAR_EXTENSION_ATTRIBUTE = 'data-node-toolbar-extension';

export const DEFAULT_CANVAS_CONFIG: CanvasConfig = {
  elementsSelectable: true,
  nodesDraggable: true,
  panOnDrag: [MIDDLE_MOUSE_BUTTON],
  selectionOnDrag: true,
  // Empty-pane zoom fights node double-click (crop / group drill-down). Scroll and toolbar zoom still work.
  zoomOnDoubleClick: false,
};

/** View-only select mode: nodes are selectable but not draggable, pan with middle mouse */
export const READONLY_CANVAS_CONFIG: CanvasConfig = {
  elementsSelectable: true,
  nodesDraggable: false,
  panOnDrag: [MIDDLE_MOUSE_BUTTON],
  selectionOnDrag: true,
  zoomOnDoubleClick: false,
};

/** Spread on top of per-transform configs: restricts pan to middle mouse and disables double-click zoom to avoid interfering with transform interactions */
export const TRANSFORM_CANVAS_CONFIG: CanvasConfig = {
  panOnDrag: [MIDDLE_MOUSE_BUTTON],
  zoomOnDoubleClick: false,
};

/** Hand (grab) tool active: disables selection/dragging so all mouse buttons pan the canvas */
export const HAND_TOOL_CANVAS_CONFIG: CanvasConfig = {
  elementsSelectable: false,
  nodesDraggable: false,
  panOnDrag: [LEFT_MOUSE_BUTTON, MIDDLE_MOUSE_BUTTON],
  selectionOnDrag: false,
  zoomOnDoubleClick: false,
};

/** Pointer must rest on a node this long before its action toolbar appears, so crossing a dense canvas doesn't flash toolbars. */
export const NODE_TOOLBAR_HOVER_ENTER_DELAY_MS = 200;

/** Toolbar lingers this long after the pointer leaves, so it survives the trip across the gap between node and toolbar. */
export const NODE_TOOLBAR_HOVER_LEAVE_DELAY_MS = 240;
